'use client';

/**
 * AudioReelPlayer — voice-actor demo reel player with waveform scrubber.
 * Adapted from the Claude Design profile-handoff bundle.
 *
 * The handoff version had a `require('./useAudio')` for live FFT-derived
 * peaks; that's a Phase 2 enhancement. This MVP version uses the
 * deterministic placeholder waveform from `makePeaks(seed)` only — visual
 * is identical, peaks just don't reflect actual amplitude.
 *
 * Duration is optional. If omitted (or 0), the component reads it from
 * the audio element's `onLoadedMetadata` and rerenders.
 */

import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface AudioReelPlayerProps {
  src?: string;
  title: string;
  subtitle?: string;
  /** seconds — if omitted, taken from the audio element on metadata load. */
  duration?: number;
  /** 0..1, ~60–120 entries for best visuals. */
  peaks?: number[];
  /** fallback peak generator seed */
  seed?: number;
  allowDownload?: boolean;
  liked?: boolean;
  onLike?: (next: boolean) => void;
  onShare?: () => void;
  onDownload?: () => void;
}

const SPEEDS = [0.75, 1, 1.25, 1.5] as const;

function fmt(t: number) {
  if (!isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

function makePeaks(seed = 1, n = 90): number[] {
  const peaks: number[] = [];
  let s = seed;
  for (let i = 0; i < n; i++) {
    s = (s * 9301 + 49297) % 233280;
    const r = s / 233280;
    const center = (n - 1) / 2;
    const dist = Math.abs(i - center) / center;
    const env = 0.45 + (1 - dist) * 0.55;
    const noise = 0.4 + r * 0.6;
    peaks.push(Math.max(0.12, Math.min(1, env * noise)));
  }
  return peaks;
}

export function AudioReelPlayer({
  src,
  title,
  subtitle,
  duration: durationProp = 0,
  peaks,
  seed = 7,
  allowDownload = false,
  liked: likedProp,
  onLike,
  onShare,
  onDownload,
}: AudioReelPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(durationProp || 0);
  const [speed, setSpeed] = useState<number>(1);
  const [vol, setVol] = useState(0.85);
  const [muted, setMuted] = useState(false);
  const [showSpeed, setShowSpeed] = useState(false);
  const [showVol, setShowVol] = useState(false);
  const [internalLiked, setInternalLiked] = useState(false);
  const liked = likedProp ?? internalLiked;

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastT = useRef(0);

  const peakData = useMemo(() => peaks ?? makePeaks(seed, 90), [peaks, seed]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !src) return;
    a.playbackRate = speed;
    a.volume = muted ? 0 : vol;
  }, [speed, vol, muted, src]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !src) return;
    if (playing) a.play().catch(() => setPlaying(false));
    else a.pause();
  }, [playing, src]);

  // Simulated playback when src is missing — used by editor previews
  // before storage upload completes.
  useEffect(() => {
    if (src) return;
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }
    if (!duration) return;
    lastT.current = performance.now();
    const tick = (now: number) => {
      const dt = (now - lastT.current) / 1000;
      lastT.current = now;
      setTime((t) => {
        const next = t + dt * speed;
        if (next >= duration) {
          setPlaying(false);
          return 0;
        }
        return next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [playing, speed, duration, src]);

  const seek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = waveRef.current;
      if (!el || !duration) return;
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const next = Math.max(0, Math.min(duration, x * duration));
      setTime(next);
      const a = audioRef.current;
      if (a && src) a.currentTime = next;
    },
    [duration, src]
  );

  const toggleLike = () => {
    const next = !liked;
    if (likedProp === undefined) setInternalLiked(next);
    onLike?.(next);
  };

  const progress = duration ? time / duration : 0;
  const currentBar = Math.floor(progress * peakData.length);

  return (
    <div
      className="relative grid items-center gap-4 rounded-xl border border-border-subtle bg-surface-2 p-4
                 [grid-template-columns:auto_1fr_auto]
                 md:gap-4 max-md:[grid-template-columns:auto_1fr] max-md:gap-3"
    >
      {src && (
        <audio
          ref={audioRef}
          src={src}
          preload="metadata"
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (isFinite(d) && d > 0) setDuration(d);
          }}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          onEnded={() => {
            setPlaying(false);
            setTime(0);
          }}
        />
      )}

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setPlaying((p) => !p);
        }}
        aria-label={playing ? 'Pause' : 'Play'}
        className="grid h-14 w-14 place-items-center rounded-full border-0 bg-accent text-text-on-accent
                   shadow-sm transition hover:scale-105 hover:bg-accent-hover
                   active:scale-[0.98] max-md:h-12 max-md:w-12"
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>

      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex items-baseline justify-between gap-3.5">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold leading-snug text-text-primary">{title}</div>
            {subtitle && <div className="mt-0.5 text-[11.5px] text-text-tertiary">{subtitle}</div>}
          </div>
          <div className="shrink-0 font-mono text-[11.5px] tabular-nums text-text-secondary">
            <strong className="font-medium text-accent">{fmt(time)}</strong>
            <span className="text-text-tertiary"> / {fmt(duration)}</span>
          </div>
        </div>

        <div
          ref={waveRef}
          onClick={seek}
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.max(0, Math.round(duration))}
          aria-valuenow={Math.round(time)}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setTime((t) => Math.max(0, t - 5));
            if (e.key === 'ArrowRight') setTime((t) => Math.min(duration, t + 5));
          }}
          className="flex h-11 cursor-pointer select-none items-center gap-[2px] px-px"
        >
          {peakData.map((h, i) => {
            const played = i < currentBar;
            const cur = i === currentBar;
            return (
              <span
                key={i}
                className={[
                  'min-h-[3px] flex-1 rounded-[1.5px] transition-[background-color,transform] duration-150',
                  played
                    ? 'bg-[var(--wave-progress)]'
                    : 'bg-[var(--wave-track)]',
                  cur && '!bg-accent-hover scale-y-110',
                  cur && playing && 'arp-bar-pulse',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ height: `${h * 100}%` }}
              />
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-1.5 max-md:col-span-2 max-md:justify-between max-md:border-t max-md:border-white/[0.05] max-md:pt-3">
        <IconButton onClick={toggleLike} ariaLabel="Like reel" pressed={liked} className={liked ? 'text-accent' : ''}>
          <HeartIcon filled={liked} />
        </IconButton>

        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowSpeed((s) => !s);
            }}
            className="h-7 cursor-pointer rounded-md border border-border-subtle bg-surface-3 px-2 font-mono text-[11px] font-semibold tabular-nums text-text-secondary transition hover:bg-surface-2"
          >
            {speed}x
          </button>
          {showSpeed && (
            <div
              onMouseLeave={() => setShowSpeed(false)}
              className="absolute bottom-[calc(100%+8px)] right-0 z-10 min-w-20 rounded-md border border-border-default bg-surface-2 p-1 shadow-2xl"
            >
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSpeed(s);
                    setShowSpeed(false);
                  }}
                  className={`block w-full rounded px-2.5 py-1.5 text-left font-mono text-xs tabular-nums transition ${
                    speed === s
                      ? 'bg-accent-soft text-accent'
                      : 'text-text-secondary hover:bg-surface-3'
                  }`}
                >
                  {s}x
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <IconButton onClick={() => setShowVol((v) => !v)} ariaLabel="Volume">
            {muted ? <SpeakerOffIcon /> : <SpeakerIcon />}
          </IconButton>
          {showVol && (
            <div
              onMouseLeave={() => setShowVol(false)}
              className="absolute bottom-[calc(100%+8px)] left-1/2 z-10 -translate-x-1/2 rounded-md border border-border-default bg-surface-2 p-2 shadow-2xl"
            >
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : vol}
                onChange={(e) => {
                  const v = +e.target.value;
                  setVol(v);
                  setMuted(v === 0);
                }}
                className="h-1 w-24 appearance-none rounded bg-surface-3 outline-none [accent-color:var(--accent)]"
              />
            </div>
          )}
        </div>

        {allowDownload && (
          <IconButton onClick={onDownload} ariaLabel="Download">
            <DownloadIcon />
          </IconButton>
        )}
        <IconButton onClick={onShare} ariaLabel="Share">
          <ShareIcon />
        </IconButton>
      </div>
    </div>
  );
}

function IconButton({
  children,
  onClick,
  ariaLabel,
  pressed,
  className,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  ariaLabel: string;
  pressed?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      aria-label={ariaLabel}
      aria-pressed={pressed}
      className={`grid h-9 w-9 place-items-center rounded-md border-0 bg-transparent text-text-tertiary transition hover:bg-surface-3 hover:text-text-primary ${className ?? ''}`}
    >
      {children}
    </button>
  );
}

const PlayIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5.5v13l11-6.5L8 5.5z" />
  </svg>
);
const PauseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <rect x="6.5" y="5" width="3.5" height="14" rx="1" />
    <rect x="14" y="5" width="3.5" height="14" rx="1" />
  </svg>
);
const HeartIcon = ({ filled }: { filled: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M9 14.5c-3-1.8-6-4.2-6-7.2A3.3 3.3 0 016.3 4 3 3 0 019 5.6 3 3 0 0111.7 4 3.3 3.3 0 0115 7.3c0 3-3 5.4-6 7.2z" />
  </svg>
);
const SpeakerIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
    <path d="M3 6.5h2.5L9 4v10L5.5 11.5H3v-5z" />
    <path d="M11.5 6.5a3 3 0 010 5" />
  </svg>
);
const SpeakerOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round">
    <path d="M3 6.5h2.5L9 4v10L5.5 11.5H3v-5z" />
    <path d="M12 7l4 4M16 7l-4 4" />
  </svg>
);
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3v9M5 8.5l4 4 4-4M3.5 14.5h11" />
  </svg>
);
const ShareIcon = () => (
  <svg width="16" height="16" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5" cy="9" r="2" />
    <circle cx="13" cy="4.5" r="2" />
    <circle cx="13" cy="13.5" r="2" />
    <path d="M6.7 8l4.6-2.6M6.7 10l4.6 2.6" />
  </svg>
);

export default AudioReelPlayer;
